import React, {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';

import {Button, Dialog, DialogContent, DialogTitle, FormControl, Stack, TextField, Typography} from '@mui/material';

import {CREATE_TAG, FETCH_INTERNAL_TAGS, UPDATE_TAG} from '../../queries/GraphQLQueries';
import {TAG_TYPE} from '../../helpers/TagHelper';

import CategorySelect from './CategorySelect';
import TagTypeToggle from './TagTypeToggle';
import {SENTIMENT} from "../../helpers/SessionHelper";

const styles = {
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        minWidth: '30rem',
        paddingY: '1rem',
    },
    dialogTitle: {
        paddingBottom: '0.5rem',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        marginTop: '1rem',
    }
};

export default function AddEditTagDialog({open, onClose, tag = null}) {
    const [category, setCategory] = useState(TAG_TYPE.BEHAVIOR);
    const [tagName, setTagName] = useState('');
    const [tagType, setTagType] = useState(SENTIMENT.POSITIVE);
    const [canEnableSubmit, setCanEnableSubmit] = useState(false);

    const isEditMode = tag !== null;

    const [createTag, {loading: isCreateTagInProgress, error: createError, reset: resetCreateTag}] = useMutation(CREATE_TAG, {
        refetchQueries: [{query: FETCH_INTERNAL_TAGS}],
        onCompleted: () => handleClose()
    });

    const [updateTag, {loading: isUpdateTagLoading, error: updateError, reset: resetUpdateTag}] = useMutation(UPDATE_TAG, {
        refetchQueries: [{query: FETCH_INTERNAL_TAGS}],
        onCompleted: () => handleClose()
    });

    const error = createError || updateError;
    const isLoading = isCreateTagInProgress || isUpdateTagLoading;

    useEffect(() => {
        if (tag) {
            setCategory(tag.category);
            setTagName(tag.label);
            setTagType(tag.sentiment);
        }
    }, [tag]);

    useEffect(() => {
        const hasChanges = isEditMode
            ? category !== tag?.category || tagType !== tag?.sentiment || tagName !== tag?.label
            : true;

        const shouldEnable = category.trim() !== '' && tagName.trim() !== '' && hasChanges;
        setCanEnableSubmit(shouldEnable);
    }, [category, tagName, tagType, tag, isEditMode]);

    const handleCategoryChange = (event) => setCategory(event.target.value);
    const handleNameChange = (event) => setTagName(event.target.value);
    const handleTagTypeChange = (newTagType) => setTagType(newTagType);

    const handleSubmit = async () => {
        try {
            if (isEditMode) {
                await updateTag({
                    variables: {
                        tagId: tag.id,
                        name: tagName.trim(),
                        internalName: tagName.trim(),
                        type: category,
                        sentiment: tagType,
                    }
                });
            } else {
                await createTag({
                    variables: {
                        name: tagName.trim(),
                        internalName: tagName.trim(),
                        type: category,
                        sentiment: tagType,
                    }
                });
            }
        } catch (err) {
            console.error(`Failed to ${isEditMode ? 'update' : 'create'} tag:`, err);
        }
    };

    const handleClose = () => {
        setCategory(TAG_TYPE.BEHAVIOR);
        setTagName('');
        setTagType(SENTIMENT.POSITIVE);
        resetCreateTag();
        resetUpdateTag();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} disableRestoreFocus>
            <DialogTitle sx={styles.dialogTitle}>
                <Typography variant="h6">{isEditMode ? 'Edit Tag' : 'Add New Tag'}</Typography>
            </DialogTitle>
            <DialogContent sx={styles.dialogContent}>
                <CategorySelect value={category} onChange={handleCategoryChange}/>

                <TagTypeToggle value={tagType} onChange={handleTagTypeChange} />

                <FormControl fullWidth>
                    <TextField
                        label="Name"
                        value={tagName}
                        onChange={handleNameChange}
                        fullWidth
                        placeholder="Enter the display name for this tag"
                        error={!!error}
                        helperText={error?.message || ''}/>
                </FormControl>

                <Stack direction="row" sx={styles.buttonContainer}>
                    <Button variant="contained" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={!canEnableSubmit || isCreateTagInProgress}>
                        {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update' : 'Create')}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}